import type {
  BlackAndWhiteState,
  TileNumber,
} from "@cloudnest/black-and-white-engine";

export type BlackAndWhiteReplayEvent =
  | {
      readonly eventType: "init";
      readonly userId: string;
      readonly payload: { readonly state: BlackAndWhiteState };
    }
  | {
      readonly eventType: "SyncViewerState";
      readonly userId: string;
      readonly payload?: { readonly actionId?: string };
    }
  | {
      readonly eventType: "SubmitTile";
      readonly userId: string;
      readonly payload: { readonly tile: TileNumber };
    };

export type BlackAndWhiteReplayEventType =
  BlackAndWhiteReplayEvent["eventType"];

export interface BlackAndWhiteEventInput {
  readonly eventType: string;
  readonly userId?: string;
  readonly payload?: Record<string, unknown>;
}

export const BLACK_AND_WHITE_REPLAY_EVENT_TYPES = [
  "init",
  "SyncViewerState",
  "SubmitTile",
] as const satisfies readonly BlackAndWhiteReplayEventType[];

function requireUserId(event: BlackAndWhiteEventInput): string {
  if (typeof event.userId === "string" && event.userId.length > 0) {
    return event.userId;
  }
  throw new Error("Black and White replay event is missing userId");
}

export function normalizeBlackAndWhiteReplayEventType(
  eventType: string,
): BlackAndWhiteReplayEventType {
  switch (eventType) {
    case "init":
    case "SyncViewerState":
    case "SubmitTile":
      return eventType;
    default:
      throw new Error(`Unsupported Black and White event type: ${eventType}`);
  }
}

export function toBlackAndWhiteReplayEvent(
  event: BlackAndWhiteEventInput,
): BlackAndWhiteReplayEvent {
  const eventType = normalizeBlackAndWhiteReplayEventType(event.eventType);
  const userId = requireUserId(event);
  const payload = event.payload ?? {};

  switch (eventType) {
    case "init":
      return {
        eventType,
        userId,
        payload: payload as { readonly state: BlackAndWhiteState },
      };
    case "SyncViewerState":
      return {
        eventType,
        userId,
        payload: payload as { readonly actionId?: string },
      };
    case "SubmitTile":
      return {
        eventType,
        userId,
        payload: payload as { readonly tile: TileNumber },
      };
  }
}
